import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, useNavigate } from "react-router-dom";
import { ClerkProvider } from "@clerk/clerk-react";
import { AppRouter } from "@/AppRouter";
import { Providers } from "@/providers/Providers";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { initializePublicRuntimeConfig } from "@/lib/config/public-runtime-config";
import { debugLog } from "@/lib/utils/debug";
import "@/fonts.css";
import "@/globals.css";

const runtimeConfig = initializePublicRuntimeConfig();

const clerkPublishableKey = String(
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? ""
);

if (!clerkPublishableKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

debugLog("[Mirage] Runtime config", {
  apiUrl: runtimeConfig.apiUrl,
  websocketUrl: runtimeConfig.websocketUrl,
  clerkPublishableKey,
});

function AppWithClerk({ publishableKey }: { publishableKey: string }) {
  const navigate = useNavigate();

  return (
    <ClerkProvider
      publishableKey={publishableKey}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      afterSignOutUrl="/"
      routerPush={(to) => navigate(to)}
      routerReplace={(to) => navigate(to, { replace: true })}
    >
      <Providers>
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </Providers>
    </ClerkProvider>
  );
}

const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element #root not found in DOM");
}

ReactDOM.createRoot(rootElement).render(
  <React.StrictMode>
    <BrowserRouter>
      <AppWithClerk publishableKey={clerkPublishableKey} />
    </BrowserRouter>
  </React.StrictMode>
);
